import {
  BadgeCheck,
  BadgePercent,
  Ban,
  FileImage,
  Gift,
  LogOut,
  MessageSquareText,
  RefreshCw,
  ScrollText,
  Settings,
  ShieldCheck,
  Store,
  UserRoundPlus,
  Users,
  Wallet,
  WalletCards
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { adminRequest } from "./api";
import { PromotionEditor } from "./PromotionEditor";
import { ReviewsPanel } from "./ReviewsPanel";
import {
  SellerBonusesPanel,
  SellerCommercialEditor,
  SellerConfigPanel,
  SellerInvitationsPanel,
  ShareableContentPanel,
  WalletAdjustmentPanel
} from "./SellerAdminPanels";
import { useAdminAuth } from "./AuthProvider";
import type {
  AuditEvent,
  PayoutRequest,
  PayoutStatus,
  Promotion,
  PromotionInput,
  Seller,
  SellerApplication,
  SellerStatus
} from "./types";

type Section = "overview" | "promotions" | "reviews" | "applications" | "sellers" | "invitations" | "sellerConfig" | "content" | "bonuses" | "walletAdjustments" | "payouts" | "audit";

type AdminState = {
  applications: SellerApplication[];
  promotions: Promotion[];
  sellers: Seller[];
  payouts: PayoutRequest[];
  audit: AuditEvent[];
};

const emptyState: AdminState = {
  applications: [],
  promotions: [],
  sellers: [],
  payouts: [],
  audit: []
};

const navItems: Array<{ id: Section; label: string; icon: LucideIcon }> = [
  { id: "overview", label: "Resumen", icon: ShieldCheck },
  { id: "promotions", label: "Promociones", icon: BadgePercent },
  { id: "reviews", label: "Reviews", icon: MessageSquareText },
  { id: "applications", label: "Solicitudes", icon: Users },
  { id: "sellers", label: "Sellers", icon: Store },
  { id: "invitations", label: "Invitaciones", icon: UserRoundPlus },
  { id: "sellerConfig", label: "Config seller", icon: Settings },
  { id: "content", label: "Contenido", icon: FileImage },
  { id: "bonuses", label: "Bonos", icon: Gift },
  { id: "walletAdjustments", label: "Wallet", icon: Wallet },
  { id: "payouts", label: "Retiros", icon: WalletCards },
  { id: "audit", label: "Auditoria", icon: ScrollText }
];

function formatDate(value?: string) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function formatMoney(amount: number, currency: string) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency,
    maximumFractionDigits: 0
  }).format(amount);
}

function StatusBadge({ status }: { status: SellerStatus | PayoutStatus }) {
  return <span className={`status status-${status}`}>{status}</span>;
}

function LoginScreen() {
  const { signIn, isLoading } = useAdminAuth();

  return (
    <main className="login-shell">
      <section className="login-panel">
        <div className="mark"><ShieldCheck size={28} /></div>
        <h1>Store Admin</h1>
        <p>Consola privada para sellers, retiros y auditoria.</p>
        <button className="primary-button" type="button" onClick={signIn} disabled={isLoading}>
          Ingresar con Google
        </button>
      </section>
    </main>
  );
}

function AccessDenied() {
  const { user, signOutAdmin, refreshToken } = useAdminAuth();

  return (
    <main className="login-shell">
      <section className="login-panel">
        <div className="mark danger"><Ban size={28} /></div>
        <h1>Acceso restringido</h1>
        <p>{user?.email} no tiene claim admin activo.</p>
        <div className="button-row">
          <button className="secondary-button" type="button" onClick={() => void refreshToken()}>
            Actualizar claims
          </button>
          <button className="ghost-button" type="button" onClick={signOutAdmin}>
            Salir
          </button>
        </div>
      </section>
    </main>
  );
}

function StatCard({ label, value, detail }: { label: string; value: number; detail: string }) {
  return (
    <article className="stat-card">
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

export default function App() {
  const auth = useAdminAuth();
  const [section, setSection] = useState<Section>("overview");
  const [data, setData] = useState<AdminState>(emptyState);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null | undefined>(undefined);
  const [editingSeller, setEditingSeller] = useState<Seller | null>(null);

  const pendingApplications = useMemo(
    () => data.applications.filter((item) => item.status === "pending"),
    [data.applications]
  );
  const requestedPayouts = useMemo(
    () => data.payouts.filter((item) => item.status === "requested"),
    [data.payouts]
  );

  const loadData = useCallback(async () => {
    if (!auth.token || !auth.isAdmin) return;

    setIsRefreshing(true);
    setError(null);

    try {
      const [applications, promotions, sellers, payouts, audit] = await Promise.all([
        adminRequest<SellerApplication[]>(auth.token, "/v1/admin/sellers/applications"),
        adminRequest<Promotion[]>(auth.token, "/v1/admin/promotions"),
        adminRequest<Seller[]>(auth.token, "/v1/admin/sellers"),
        adminRequest<PayoutRequest[]>(auth.token, "/v1/admin/payouts"),
        adminRequest<AuditEvent[]>(auth.token, "/v1/admin/audit")
      ]);

      setData({ applications, promotions, sellers, payouts, audit });
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "No se pudo cargar admin.");
    } finally {
      setIsRefreshing(false);
    }
  }, [auth.isAdmin, auth.token]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  async function updateApplication(applicationId: string, status: SellerStatus) {
    if (!auth.token) return;
    setError(null);
    try {
      await adminRequest<SellerApplication>(auth.token, `/v1/admin/sellers/applications/${applicationId}`, {
        method: "PATCH",
        body: JSON.stringify({ status })
      });
      await loadData();
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : "No se pudo actualizar la solicitud.");
    }
  }

  async function updateSeller(sellerId: string, status: SellerStatus) {
    if (!auth.token) return;
    await adminRequest<Seller>(auth.token, `/v1/admin/sellers/${sellerId}`, {
      method: "PATCH",
      body: JSON.stringify({ status })
    });
    setEditingSeller(null);
    await loadData();
  }

  async function updatePayout(payoutId: string, status: PayoutStatus) {
    if (!auth.token) return;
    setError(null);
    try {
      await adminRequest<PayoutRequest>(auth.token, `/v1/admin/payouts/${payoutId}`, {
        method: "PATCH",
        body: JSON.stringify({ status })
      });
      await loadData();
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : "No se pudo actualizar el retiro.");
    }
  }

  async function savePromotion(input: PromotionInput, isNew: boolean) {
    if (!auth.token) return;
    const path = isNew ? "/v1/admin/promotions" : `/v1/admin/promotions/${input.id}`;
    const body = isNew ? input : Object.fromEntries(
      Object.entries(input).filter(([key]) => key !== "id")
    );

    await adminRequest<Promotion>(auth.token, path, {
      method: isNew ? "POST" : "PUT",
      body: JSON.stringify(body)
    });
    setEditingPromotion(undefined);
    await loadData();
  }

  if (auth.isLoading) {
    return <main className="loading">Cargando sesion...</main>;
  }

  if (!auth.user) {
    return <LoginScreen />;
  }

  if (!auth.isAdmin) {
    return <AccessDenied />;
  }

  return (
    <main className="admin-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="mark"><ShieldCheck size={22} /></div>
          <div>
            <strong>Store Admin</strong>
            <span>Baseline</span>
          </div>
        </div>

        <nav>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={section === item.id ? "active" : ""}
                type="button"
                onClick={() => setSection(item.id)}
              >
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <section className="content">
        <header className="topbar">
          <div>
            <p>Admin</p>
            <h1>{navItems.find((item) => item.id === section)?.label}</h1>
          </div>
          <div className="session">
            <span>{auth.user.email}</span>
            <button className="icon-button" type="button" onClick={() => void loadData()} disabled={isRefreshing}>
              <RefreshCw size={18} className={isRefreshing ? "spin" : ""} />
            </button>
            <button className="icon-button" type="button" onClick={auth.signOutAdmin}>
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {error ? <div className="error-banner">{error}</div> : null}

        {section === "overview" ? (
          <div className="grid stats-grid">
            <StatCard label="Solicitudes pendientes" value={pendingApplications.length} detail="Sellers esperando revision" />
            <StatCard label="Sellers aprobados" value={data.sellers.filter((item) => item.status === "approved").length} detail="Usuarios con claim seller" />
            <StatCard label="Retiros solicitados" value={requestedPayouts.length} detail="Payouts por aprobar" />
            <StatCard label="Eventos admin" value={data.audit.length} detail="Ultimos movimientos registrados" />
          </div>
        ) : null}

        {section === "promotions" ? (
          <div className="promotion-admin-layout">
            {editingPromotion !== undefined ? (
              <PromotionEditor
                promotion={editingPromotion}
                onSave={savePromotion}
                onCancel={() => setEditingPromotion(undefined)}
              />
            ) : (
              <TableShell
                title="Promociones posicionales"
                action={(
                  <button className="primary-button" type="button" onClick={() => setEditingPromotion(null)}>
                    Nueva promoción
                  </button>
                )}
              >
                <table>
                  <thead>
                    <tr>
                      <th>Promoción</th>
                      <th>Estado</th>
                      <th>Regla</th>
                      <th>Vigencia</th>
                      <th>Versión</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.promotions.map((promotion) => (
                      <tr key={promotion.id}>
                        <td><strong>{promotion.name}</strong><br /><small>{promotion.presentation.headline}</small></td>
                        <td><span className={`status status-${promotion.status}`}>{promotion.status}</span></td>
                        <td><small>{promotion.rule.tiers.map((tier) => {
                          const range = tier.maxQuantity === null
                            ? `${tier.minQuantity}+`
                            : tier.maxQuantity === tier.minQuantity
                              ? `${tier.minQuantity}`
                              : `${tier.minQuantity}-${tier.maxQuantity}`;
                          const steps = tier.steps.map((step) => (
                            "position" in step
                              ? `${step.position}ª=${step.payPct}%`
                              : `${step.fromPosition}ª+=${step.payPct}%`
                          )).join(", ");
                          return `${range} uds: ${steps}`;
                        }).join(" · ")}</small></td>
                        <td>{formatDate(promotion.startsAt || undefined)}<br /><small>hasta {promotion.endsAt ? formatDate(promotion.endsAt) : "sin límite"}</small></td>
                        <td>v{promotion.version}</td>
                        <td>
                          <button className="secondary-button" type="button" onClick={() => setEditingPromotion(promotion)}>
                            Editar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableShell>
            )}
          </div>
        ) : null}

        {section === "applications" ? (
          <TableShell title="Solicitudes seller">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Contacto</th>
                  <th>Estado</th>
                  <th>Invitacion</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.applications.map((application) => {
                  const approveLabel = application.status === "approved" ? "Reaplicar" : "Aprobar";
                  return (
                    <tr key={application.id}>
                      <td>{application.fullName}</td>
                      <td>{application.email}<br /><small>{application.phone}</small></td>
                      <td>
                        <StatusBadge status={application.status} />
                        {application.appliedSellerMode ? <><br /><small>Aplicado: {application.appliedSellerMode}</small></> : null}
                      </td>
                      <td>
                        {application.invitationType || "-"}
                        {application.invitedBySellerName ? <><br /><small>{application.invitedBySellerName}</small></> : null}
                        {application.targetSellerMode ? <><br /><small>Target: {application.targetSellerMode}{application.targetCommissionLevel ? ` / ${application.targetCommissionLevel}` : ""}</small></> : null}
                      </td>
                      <td>{formatDate(application.createdAt)}</td>
                      <td>
                        <div className="table-actions">
                          <button type="button" onClick={() => void updateApplication(application.id, "approved")}>
                            <BadgeCheck size={16} /> {approveLabel}
                          </button>
                          <button type="button" onClick={() => void updateApplication(application.id, "rejected")}>
                            Rechazar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableShell>
        ) : null}

        {section === "reviews" ? <ReviewsPanel /> : null}

        {section === "sellers" ? (
          <div className="promotion-admin-layout">
            {editingSeller ? (
              <SellerCommercialEditor
                seller={editingSeller}
                sellers={data.sellers}
                onCancel={() => setEditingSeller(null)}
                onSaved={async () => {
                  setEditingSeller(null);
                  await loadData();
                }}
              />
            ) : null}
            <TableShell title="Sellers">
              <table>
                <thead>
                  <tr>
                    <th>Seller</th>
                    <th>Codigo</th>
                    <th>Modo/Nivel</th>
                    <th>Lider</th>
                    <th>Comision</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sellers.map((seller) => {
                    const effectiveRate = seller.commissionOverride?.active && typeof seller.commissionOverride.rate === "number"
                      ? seller.commissionOverride.rate
                      : seller.commissionRate;
                    return (
                      <tr key={seller.id}>
                        <td>{seller.fullName}<br /><small>{seller.email}</small></td>
                        <td>{seller.code}</td>
                        <td>{seller.sellerMode || "-"}<br /><small>{seller.commissionLevel || seller.level || "-"}</small></td>
                        <td>
                          {seller.leaderSellerId
                            ? data.sellers.find((item) => item.id === seller.leaderSellerId)?.fullName || seller.leaderSellerId
                            : "-"}
                        </td>
                        <td>
                          {effectiveRate === undefined ? "Default" : `${Math.round(effectiveRate * 100)}%`}
                          {seller.commissionOverride?.active ? <><br /><small>Override activo</small></> : null}
                        </td>
                        <td><StatusBadge status={seller.status} /></td>
                        <td>
                          <div className="table-actions">
                            <button type="button" onClick={() => setEditingSeller(seller)}>Editar comercial</button>
                            <button type="button" onClick={() => void updateSeller(seller.id, "approved")}>Activar</button>
                            <button type="button" onClick={() => void updateSeller(seller.id, "suspended")}>Suspender</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableShell>
          </div>
        ) : null}

        {section === "payouts" ? (
          <TableShell title="Retiros">
            <table>
              <thead>
                <tr>
                  <th>Seller</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.payouts.map((payout) => (
                  <tr key={payout.id}>
                    <td>{payout.sellerId}</td>
                    <td>{formatMoney(payout.amount, payout.currency)}</td>
                    <td><StatusBadge status={payout.status} /></td>
                    <td>{formatDate(payout.createdAt)}</td>
                    <td>
                      <div className="table-actions">
                        <button type="button" onClick={() => void updatePayout(payout.id, "approved")}>Aprobar</button>
                        <button type="button" onClick={() => void updatePayout(payout.id, "paid")}>Pagado</button>
                        <button type="button" onClick={() => void updatePayout(payout.id, "rejected")}>Rechazar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableShell>
        ) : null}

        {section === "sellerConfig" ? <SellerConfigPanel /> : null}
        {section === "invitations" ? <SellerInvitationsPanel sellers={data.sellers} /> : null}
        {section === "content" ? <ShareableContentPanel /> : null}
        {section === "bonuses" ? <SellerBonusesPanel /> : null}
        {section === "walletAdjustments" ? <WalletAdjustmentPanel /> : null}

        {section === "audit" ? (
          <TableShell title="Auditoria">
            <table>
              <thead>
                <tr>
                  <th>Accion</th>
                  <th>Target</th>
                  <th>Admin</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {data.audit.map((event) => (
                  <tr key={event.id}>
                    <td>{event.action}</td>
                    <td>{event.targetType}<br /><small>{event.targetId}</small></td>
                    <td>{event.adminUid}</td>
                    <td>{formatDate(event.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableShell>
        ) : null}
      </section>
    </main>
  );
}

function TableShell({
  title,
  action,
  children
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="table-card">
      <div className="table-header">
        <h2>{title}</h2>
        {action}
      </div>
      <div className="table-scroll">{children}</div>
    </section>
  );
}
